import { useEffect, useRef, type ReactNode } from 'react';
import PersonIcon from '@mui/icons-material/Person';
import {
    FilterList,
    FilterListItem,
    useGetIdentity,
    useGetList,
    useListContext,
} from 'react-admin';

import { ListFilterSidebar } from '@/components/filters/ListFilterSidebar';
import { User } from '@/types';

type IdentityWithAdmin = {
    id: string | number;
    fullName?: string;
    administrator?: boolean;
};

const AllUsersFilterItem = () => (
    <FilterListItem
        label="All"
        value={{}}
        isSelected={(_value, filters) => filters.created_by == null}
        toggleFilter={(_value, filters) => {
            const { created_by: _, ...rest } = filters;
            return rest;
        }}
    />
);

/** Superuser: All + every user. Non-superuser: All + current user only. */
export const CreatedByUserFilter = ({
    searchPlaceholder,
    children,
}: {
    searchPlaceholder: string;
    children?: ReactNode;
}) => {
    const { identity } = useGetIdentity();
    const current = identity as IdentityWithAdmin | undefined;
    const isAdmin = Boolean(current?.administrator);

    const { data: users } = useGetList<User>(
        'users',
        {
            pagination: { page: 1, perPage: 200 },
            sort: { field: 'name', order: 'ASC' },
        },
        { enabled: isAdmin },
    );

    return (
        <ListFilterSidebar searchPlaceholder={searchPlaceholder}>
            <FilterList label="User" icon={<PersonIcon />}>
                <AllUsersFilterItem />
                {isAdmin
                    ? (users ?? []).map(user => (
                          <FilterListItem
                              key={user.id}
                              label={user.name || `User #${user.id}`}
                              value={{ created_by: user.id }}
                          />
                      ))
                    : current && (
                          <FilterListItem
                              label={current.fullName || `User #${current.id}`}
                              value={{ created_by: current.id }}
                          />
                      )}
            </FilterList>
            {children}
        </ListFilterSidebar>
    );
};

/** filterDefaultValues is ignored once the list store has sort/page; re-apply on mount. */
export const EnsureDefaultCurrentUserFilter = () => {
    const { identity } = useGetIdentity();
    const { filterValues, showFilter } = useListContext();
    const applied = useRef(false);

    useEffect(() => {
        const current = identity as IdentityWithAdmin | undefined;
        if (!current || current.administrator || applied.current) return;
        applied.current = true;
        if (filterValues.created_by != null) return;
        // showFilter keeps page; setFilters would reset to 1 via SET_FILTER.
        showFilter('created_by', current.id);
    }, [identity, filterValues, showFilter]);

    return null;
};

export const isAdministrator = (identity: unknown) =>
    Boolean((identity as IdentityWithAdmin | undefined)?.administrator);
